import { clear, h, link } from "./dom.ts";
import { icon } from "./icons.ts";
import { browsePage } from "./pages/browse.ts";
import { clipPage, feedPage } from "./pages/clips.ts";
import { creatorPage } from "./pages/creator.ts";
import { avatarPage } from "./pages/avatar.ts";
import { checkoutReturnPage } from "./pages/checkout.ts";
import { dashboardPage } from "./pages/dashboard.ts";
import { downloadPage } from "./pages/download.ts";
import { eventsPage } from "./pages/events.ts";
import { gamePage } from "./pages/game.ts";
import { homePage } from "./pages/home.ts";
import { signinPage } from "./pages/signin.ts";
import { assetPage, storeBrowsePage, storeCreatorPage } from "./pages/store.ts";
import { navigate, rerender, route, setTitle, startRouter } from "./router.ts";
import { currentMe, loadMe, nameOf, onSessionChange, signedIn, signOut } from "./session.ts";
import { loadViewerPrice } from "./money.ts";
import { loadSelling, selling } from "./selling.ts";

route("/", homePage);
route("/browse", browsePage);
route("/g/:id", gamePage);
route("/c/:id", creatorPage);
route("/download", downloadPage);
route("/clip/:id", clipPage);
route("/feed", feedPage);
route("/events", eventsPage);
route("/store", storeBrowsePage);
route("/store/a/:creator/:name", assetPage);
route("/store/c/:handle", storeCreatorPage);
route("/signin", signinPage);
route("/checkout/:state", checkoutReturnPage);
route("/me/avatar", avatarPage);
route("/dashboard", dashboardPage);
route("/dashboard/:section", dashboardPage);
route("/dashboard/g/:game", dashboardPage);
route("/dashboard/g/:game/:section", dashboardPage);

function notFound(params: Record<string, string>): HTMLElement {
  setTitle("Not found");
  return h(
    "div",
    { class: "empty" },
    h("h1", null, params.error ? "Something went wrong" : "Page not found"),
    h("p", { class: "dim" }, params.error ?? "That page doesn't exist."),
    link("/", { class: "btn" }, "Home"),
  );
}

/** The pages the launcher's rail lists, as the global bar's links (Feed only when signed in). */
const GLOBAL_NAV: [string, string][] = [
  ["/", "Home"],
  ["/browse", "Browse"],
  ["/store", "Assets"],
  ["/events", "Events"],
  ["/feed", "Feed"],
  ["/download", "Download"],
  ["/dashboard", "For creators"],
];

const current = (href: string) => {
  const here = location.pathname;
  return here === href || (href !== "/" && here.startsWith(href)) ? "page" : null;
};

/** The global bar, after the Epic web store's: the mark and wordmark with the rail's pages, then the account and Get Banger. */
function header(): void {
  const top = document.getElementById("top")!;
  const me = currentMe();
  clear(
    top,
    h(
      "div",
      { class: "globalbar-in" },
      link("/", { class: "brand", "aria-label": "Banger home" }, h("img", { src: "/mark/banger-mark.svg", alt: "", width: 28, height: 28 }), h("span", { class: "wordmark" }, "Banger")),
      h("nav", { class: "global-nav", "aria-label": "Main" }, GLOBAL_NAV.filter(([href]) => me || href !== "/feed").map(([href, text]) => link(href, { class: "global-link", "aria-current": current(href) }, text))),
      h("span", { class: "topbar-fill" }),
      h(
        "div",
        { class: "account" },
        me
          ? h(
              "details",
              { class: "menu" },
              h("summary", { "aria-label": "Account menu" }, h("span", { class: "avatar-badge small", "aria-hidden": "true" }, nameOf(me).replace("@", "").slice(0, 1).toUpperCase()), h("span", { class: "menu-name" }, nameOf(me))),
              h(
                "div",
                { class: "menu-list" },
                link("/feed", null, "Your feed"),
                link("/me/avatar", null, "My avatar"),
                link("/dashboard", null, "Creator dashboard"),
                h(
                  "button",
                  {
                    type: "button",
                    onclick: async () => {
                      await signOut();
                      navigate("/");
                    },
                  },
                  "Sign out",
                ),
              ),
            )
          : link(`/signin?next=${encodeURIComponent(location.pathname + location.search)}`, { class: "global-link" }, "Sign in"),
      ),
      location.pathname === "/download" ? null : link("/download", { class: "btn small get" }, "Get Banger"),
    ),
  );
  topbar();
}

/** The store bar under it: Epic's search and Discover / Browse tabs. */
function topbar(): void {
  const bar = document.getElementById("topbar");
  if (!bar) return;
  const here = location.pathname;
  document.body.dataset.route = here === "/" ? "home" : here.split("/")[1] || "home";
  const q = new URLSearchParams(location.search).get("q") ?? "";
  const input = h("input", { type: "search", name: "q", value: here === "/browse" ? q : "", placeholder: "Search games", "aria-label": "Search games", autocomplete: "off" });
  const tab = (href: string, text: string, on: boolean) => link(href, { class: "tab", "aria-current": on ? "page" : null }, text);
  clear(
    bar,
    h(
      "div",
      { class: "topbar-in" },
      h(
        "form",
        {
          class: "topbar-search",
          role: "search",
          action: "/browse",
          onsubmit: (e: Event) => {
            e.preventDefault();
            const v = (input as HTMLInputElement).value.trim();
            navigate(v ? `/browse?q=${encodeURIComponent(v)}` : "/browse");
          },
        },
        icon("search", 14),
        input,
      ),
      h("nav", { class: "tabs-line", "aria-label": "Sections" }, tab("/", "Discover", here === "/"), tab("/browse", "Browse", here === "/browse")),
    ),
  );
}

function footer(): void {
  clear(
    document.getElementById("foot")!,
    h("span", { class: "wordmark small" }, "Banger"),
    h(
      "nav",
      { "aria-label": "Footer" },
      link("/browse", null, "Browse"),
      link("/store", null, "Assets"),
      link("/events", null, "Events"),
      link("/download", null, "Download"),
      link("/dashboard", null, "For creators"),
      h("a", { href: "/install.sh" }, "install.sh"),
    ),
    h("span", { class: "dim small" }, "Good games are bangers. Games belong to their creators."),
  );
}

onSessionChange(() => {
  header();
  rerender();
  // Prices follow the viewer: their last checkout's country once signed in, the USD fallback signed out.
  if (selling()) void loadViewerPrice(signedIn()).then(rerender);
});

// close the account menu after picking something
document.addEventListener("click", (e) => {
  const menu = document.querySelector("details.menu[open]");
  if (menu && (e.target as Element).closest(".menu-list a, .menu-list button")) menu.removeAttribute("open");
});

// money's `selling` first: with it false (or money unreachable) no page shows prices, tips, earnings or checkout
void Promise.all([loadMe(), loadSelling()])
  .then(() => (selling() ? loadViewerPrice(signedIn()) : null))
  .then(() => {
    header();
    footer();
    startRouter(document.getElementById("main")!, notFound, header, () => loadSelling());
  });
